import { type ReactNode, useEffect, useState } from 'react';
import { ApiError, api, type ConsolePrincipal, canMutate, isPlatformAdmin } from './api/client';
import { LifecycleBanner } from './components/LifecycleBanner';
import { SignedOut } from './components/SignedOut';
import { allowsPaidSetup } from './lib/entitlement';
import { useFetch } from './useFetch';
import { Fleet } from './views/Fleet';
import { ManagedVpatches } from './views/ManagedVpatches';
import { Rules } from './views/Rules';
import { SiteDetail } from './views/SiteDetail';
import { ThreatIntel } from './views/ThreatIntel';
import { VulnCatalog } from './views/VulnCatalog';

/** Minimal dependency-free hash router for the console's views. */
function useHash(): string {
  const [hash, setHash] = useState(() => window.location.hash || '#/');
  useEffect(() => {
    const onChange = () => setHash(window.location.hash || '#/');
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return hash;
}

function route(
  hash: string,
  principal: ConsolePrincipal | null,
  canEdit: boolean,
  canAddProtection: boolean,
): ReactNode {
  const path = hash.replace(/^#/, '') || '/';
  const siteId = /^\/sites\/(.+)$/.exec(path)?.[1];
  if (siteId) {
    return (
      <SiteDetail
        siteId={decodeURIComponent(siteId)}
        canEdit={canEdit}
        canAddProtection={canAddProtection}
      />
    );
  }
  if (path === '/rules') {
    return <Rules principal={principal} canEdit={canEdit} canAddProtection={canAddProtection} />;
  }
  if (path === '/catalog') return <VulnCatalog />;
  if (path === '/reputation') return <ThreatIntel canEdit={canEdit} />;
  if (path === '/patches') return <ManagedVpatches isOperator={isPlatformAdmin(principal?.role)} />;
  return (
    <Fleet
      tenantId={principal?.tenantId ?? ''}
      canEdit={canEdit}
      canAddProtection={canAddProtection}
    />
  );
}

export function App() {
  // Operator dashboard shell: resolve the principal, then route.
  const hash = useHash();
  const me = useFetch(() => api.me());
  const canEdit = canMutate(me.data?.role);
  const canAddProtection = allowsPaidSetup(me.data?.entitlement);
  const [logoutError, setLogoutError] = useState<string | null>(null);

  async function signOut() {
    setLogoutError(null);
    try {
      await api.logout();
      window.location.assign('/console/');
    } catch {
      setLogoutError('Sign-out is only available on the platform-auth console.');
    }
  }

  // No session ⇒ the sign-in screen, not a shell whose every view 401s underneath. Only 401 (not
  // signed in) gates the app; a 403 means signed in without permission, which the views explain.
  if (me.error instanceof ApiError && me.error.status === 401) {
    return <SignedOut error={me.error} />;
  }

  return (
    <div className="app">
      <header>
        <div className="brand">
          🐕 Guard Dog <span className="muted">Console</span>
        </div>
        <nav>
          <a href="#/">Fleet</a>
          <a href="#/rules">Rules</a>
          <a href="#/catalog">Catalog</a>
          <a href="#/reputation">Reputation</a>
          <a href="#/patches">Patches</a>
          {/* First-install download for the off-directory connector plugin (ADR-0014). Same-origin,
              so the Access cookie authenticates it; 404s until a release is published. */}
          <a href="/v1/console/connector/download" download>
            Connector ↓
          </a>
        </nav>
        <div className="who">
          {/* Degrade quietly — the header never shows a load/error state where a name belongs. */}
          {me.data && (
            <span className="muted">
              {me.data.userId ?? me.data.kind} · {me.data.role ?? 'no role'}
            </span>
          )}
          {me.data && (
            <button type="button" className="link-button sign-out" onClick={() => void signOut()}>
              Sign out
            </button>
          )}
          {logoutError && <span className="error">{logoutError}</span>}
        </div>
      </header>
      {me.data && <LifecycleBanner entitlement={me.data.entitlement} />}
      <main>{route(hash, me.data, canEdit, canAddProtection)}</main>
    </div>
  );
}
